import React, { useState, useRef, useCallback } from "react";
import Papa from "papaparse";
import { UploadSimple, File, XCircle } from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { toastOptions } from "../../../assets/constants";

const CsvImporter = React.memo(({ label = "Importer un fichier CSV", onImport, requiredColumns = [], accept = ".csv" }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [fileName, setFileName] = useState(null);
    const fileInputRef = useRef(null);

    const validateAndParse = useCallback(
        (file) => {
            if (!file) return;

            const extension = file.name.split(".").pop().toLowerCase();
            if (extension !== "csv") {
                toast.error("Format invalide. Veuillez importer un fichier CSV.", toastOptions);
                return;
            }

            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                encoding: "UTF-8",
                complete: (results) => {
                    if (results.errors.length > 0) {
                        const criticalErrors = results.errors.filter((e) => e.type !== "FieldMismatch");
                        if (criticalErrors.length > 0) {
                            toast.error(`Erreur de parsing CSV : ${criticalErrors[0].message}`, toastOptions);
                            return;
                        }
                    }

                    const columns = results.meta.fields || [];
                    const missingColumns = requiredColumns.filter((col) => !columns.includes(col));

                    if (missingColumns.length > 0) {
                        toast.error(`Colonnes manquantes : ${missingColumns.join(", ")}`, toastOptions);
                        return;
                    }

                    setFileName(file.name);
                    onImport(results.data, columns);
                },
                error: (error) => {
                    toast.error(`Erreur de lecture : ${error.message}`, toastOptions);
                },
            });
        },
        [onImport, requiredColumns]
    );

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            setIsDragOver(false);
            const file = e.dataTransfer.files[0];
            validateAndParse(file);
        },
        [validateAndParse]
    );

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleFileChange = useCallback(
        (e) => {
            const file = e.target.files[0];
            validateAndParse(file);
            e.target.value = "";
        },
        [validateAndParse]
    );

    const handleReset = useCallback(() => {
        setFileName(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    return (
        <div
            className={`csv-importer ${isDragOver ? "csv-importer--drag-over" : ""} ${fileName ? "csv-importer--loaded" : ""}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
        >
            <input ref={fileInputRef} type="file" accept={accept} onChange={handleFileChange} className="csv-importer__input" />

            {fileName ? (
                <div className="csv-importer__file">
                    <File size={20} weight="bold" />
                    <span className="csv-importer__filename">{fileName}</span>
                    <button className="csv-importer__reset" onClick={handleReset} title="Supprimer">
                        <XCircle size={18} weight="bold" />
                    </button>
                </div>
            ) : (
                <div className="csv-importer__placeholder" onClick={() => fileInputRef.current?.click()}>
                    <div className="csv-importer__placeholder-icon">
                        <UploadSimple size={18} weight="bold" />
                    </div>
                    <div className="csv-importer__placeholder-text">
                        <span className="csv-importer__placeholder-label">{label}</span>
                        <span className="csv-importer__hint">Glisser-déposer ou cliquer</span>
                    </div>
                </div>
            )}
        </div>
    );
});

export default CsvImporter;
